import { HEADERSTATUS, MEMBER_EVENTS } from "./constants";
import { GetEoREmployeeProfileQuery } from "types/generated/operations";
import { formatToShortDate } from "pages/employer-of-record/utils/dateUtils";

export const getStatusStyling = (
  status: string | undefined,
  styles: CSSModuleClasses
): string => {
  if (!status) {
    return "";
  }

  const statusStyle = styles[status];

  if (!statusStyle && status) {
    console.error(`Style not found for status: ${status}`);
  }

  switch (status) {
    case HEADERSTATUS.PENDING:
      return styles.pending;
    case HEADERSTATUS.SCHEDULED:
      return styles.scheduled;
    case HEADERSTATUS.COMPLETED:
      return styles.terminated;
    default:
      return "";
  }
};

export const getEmployeeStatus = (
  employeeInfo: GetEoREmployeeProfileQuery | undefined
): string => {
  if (!employeeInfo) {
    return "";
  }

  const { eorEmployeeProfile: profileInfo } = employeeInfo;
  const contractEndDate = profileInfo.contract?.endDate
    ? new Date(profileInfo.contract.endDate).getTime()
    : null;
  const nowDate = new Date().getTime();
  const startDate = profileInfo.contract?.startDate
    ? new Date(profileInfo.contract.startDate).getTime()
    : undefined;
  const employmentStatus = profileInfo.employment?.employmentStatus;

  const isScheduled =
    profileInfo.offboarding?.requestedTerminationAt !== null ||
    (contractEndDate && contractEndDate > nowDate);
  const isBeforeStartDate = startDate && startDate > nowDate && !isScheduled;
  const isPending =
    (profileInfo.onboarding?.workflowState !== "complete" &&
      !isScheduled &&
      employmentStatus !== HEADERSTATUS.COMPLETED) ||
    isBeforeStartDate;
  const isCompleted =
    (contractEndDate && contractEndDate < nowDate) ||
    employmentStatus === HEADERSTATUS.COMPLETED;

  if (isPending) {
    return HEADERSTATUS.PENDING;
  } else if (isCompleted) {
    return HEADERSTATUS.COMPLETED;
  } else if (isScheduled) {
    return HEADERSTATUS.SCHEDULED;
  }

  return "";
};

export const getMemberEventBadgeText = (latestMemberEvent: string): string => {
  switch (latestMemberEvent) {
    case MEMBER_EVENTS.NEW_HIRE_REQUESTED:
      return "Gathering Info";
    case MEMBER_EVENTS.EE_INFO_SUBMITTED:
      return "Preparing EA";
    case MEMBER_EVENTS.EA_GENERATED:
      return "Preparing EA";
    case MEMBER_EVENTS.EE_CONTACTED:
      return "Gathering Additional Info";
    case MEMBER_EVENTS.EA_SENT_FOR_SIGNATURE:
      return "Pending Signature";
    case MEMBER_EVENTS.EA_SIGNED_BY_EE:
      return "Onboarding Complete";
    default:
      return "";
  }
};

export const getMemberEventTooltipText = (
  latestMemberEvent: string,
  name: string
): string => {
  switch (latestMemberEvent) {
    case MEMBER_EVENTS.NEW_HIRE_REQUESTED:
      return "We’re collecting information from your new hire.";
    case MEMBER_EVENTS.EE_INFO_SUBMITTED:
      return "We’re creating your new hire's employment agreement and will send it to them soon.";
    case MEMBER_EVENTS.EA_GENERATED:
      return "We’re creating your new hire's employment agreement and will send it to them soon.";
    case MEMBER_EVENTS.EE_CONTACTED:
      return "We’re collecting additional information from your new hire.";
    case MEMBER_EVENTS.EA_SENT_FOR_SIGNATURE:
      return "We’re waiting for your new hire to sign their Employment Agreement.";
    case MEMBER_EVENTS.EA_SIGNED_BY_EE:
      return `${name} is ready to work!`;
    default:
      return "";
  }
};

export const getTooltipDate = (datetimeISOString: string): string => {
  if (!datetimeISOString) {
    return "";
  }

  return formatToShortDate(new Date(datetimeISOString));
};
